import { createApi } from '@reduxjs/toolkit/query/react';
import {
  axiosBaseQuery,
  createAxiosInstance,
  errorHandlingInterceptor
} from '@client';
import {
  AgentProfileRatingsResponse,
  AgentRatingSubmitDTO,
  AgentRatingResponseDTO,
  AgentRatingPerBuyerResponseDTO
} from '../../types';

const customAxiosInstance = createAxiosInstance({
  // 403 must not redirect (HLA-7582); an expired session (401) still logs out
  errorInterceptor: errorHandlingInterceptor({
    handleUnauthorizedResponse: false,
    handleUnauthenticatedResponse: true
  })
});

export const agentRatingApi = createApi({
  baseQuery: axiosBaseQuery({
    customAxiosInstance
  }),
  reducerPath: 'agent-rating_api',
  endpoints: (build) => ({
    fetchAgentRating: build.query<number, { appointmentId: number }>({
      query: ({ appointmentId }) => ({
        url: `/secured/appointment/${appointmentId}/agent-rating`,
        method: 'GET'
      })
    }),
    submitRating: build.mutation<AgentRatingResponseDTO, AgentRatingSubmitDTO>({
      query: (payload) => ({
        url: `/secured/agent-rating`,
        method: 'POST',
        data: payload
      })
    }),
    fetchAgentAverageRating: build.query<
      AgentRatingPerBuyerResponseDTO,
      { agentId: number; buyerId: number }
    >({
      query: ({ agentId, buyerId }) => ({
        url: `/secured/buyers/${buyerId}/agents/${agentId}/average-rating`,
        method: 'GET'
      })
    }),
    fetchAgentRatings: build.query<
      AgentRatingResponseDTO[],
      { agentId: number; buyerId: number }
    >({
      query: ({ agentId, buyerId }) => ({
        url: `/secured/buyers/${buyerId}/agents/${agentId}/agent-ratings`,
        method: 'GET'
      })
    }),
    fetchAgentPortRatings: build.query<
      AgentProfileRatingsResponse,
      { agentId: string; portId: string }
    >({
      query: ({ agentId, portId }) => ({
        url: `/secured/buyer/agent-ratings/${agentId}/port/${portId}`,
        method: 'GET'
      })
    })
  })
});

export const {
  useLazyFetchAgentRatingQuery,
  useSubmitRatingMutation,
  useLazyFetchAgentAverageRatingQuery,
  useLazyFetchAgentRatingsQuery,
  useFetchAgentAverageRatingQuery,
  useFetchAgentPortRatingsQuery
} = agentRatingApi;
